import { useState, type FormEvent } from 'react';
import { ApiError, api, setRefreshToken, setToken } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';
import { PasswordMatchValidator } from '../components/PasswordMatchValidator';

/**
 * Cambio de contraseña forzado (v1.3.2, flujo Opción B). El Shell la renderiza
 * cuando user.mustChangePassword === true — no es una ruta saltable. Al éxito el
 * backend emite el par access+refresh definitivo; lo guardamos y recargamos.
 */
export function ChangePasswordForcedScreen({ onLogout }: { onLogout: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const policyError = (pwd: string): string | null => {
    if (pwd.length < 8) return 'La nueva contraseña debe tener al menos 8 caracteres.';
    if (!/[A-Z]/.test(pwd)) return 'Incluye al menos una letra mayúscula.';
    if (!/[0-9]/.test(pwd)) return 'Incluye al menos un número.';
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    const pe = policyError(newPassword);
    if (pe) { setError(pe); return; }

    setSubmitting(true);
    try {
      const res = await api<{ accessToken: string; refreshToken: string }>(
        '/auth/change-password-forced',
        { method: 'POST', body: { currentPassword, newPassword } },
      );
      setToken(res.accessToken);
      setRefreshToken(res.refreshToken);
      window.location.href = '/';
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? (err.body?.message ?? 'Contraseña actual incorrecta.')
          : 'No se pudo cambiar la contraseña. Intenta de nuevo.',
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8">
        <div className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            FluidSol
          </p>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">
            Cambia tu contraseña
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Tu administrador asignó una contraseña temporal. Define una nueva para
            continuar.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Contraseña actual
            </span>
            <PasswordInput
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              required
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Nueva contraseña
            </span>
            <PasswordInput
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              required
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Confirmar nueva contraseña
            </span>
            <PasswordInput
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              required
            />
            <PasswordMatchValidator password={newPassword} confirmPassword={confirm} />
          </label>
          <p className="text-[11px] text-slate-400">
            Mínimo 8 caracteres, una mayúscula y un número.
          </p>
          {error && (
            <p className="alert-over rounded-xl px-3 py-2 text-xs">{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
          >
            {submitting ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="block w-full text-center text-xs font-medium text-slate-500 underline hover:text-slate-800"
          >
            Cerrar sesión
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordForcedScreen;
